import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {hasProperty} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Toggle} from '../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {NumberInputBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {PasswordBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/passwordBox';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useTimeout} from '../../../../../srcCommon/helpers/hookHelpers';
import {usePushEvents} from '../../../../../srcCommon/push/pushContext';
import {
  ChannelFrontMailModel,
  ChannelGmailModel,
  ChannelSmtpModel,
} from '../../../../models/api/channelModel';
import {postAsync} from '../../../../services/http/http';
import {SettingsBannersEnum, useSettingsBanners} from '../../../banners/settingsBannersContext';
import {
  renderSummaryValueForBoolean,
  SettingsAccordionFieldLine,
} from '../../../common/settingsAccordionFieldLine';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsCustomSmtpFieldProps
  extends SettingsCommonChannelFieldProps<ChannelFrontMailModel | ChannelSmtpModel | ChannelGmailModel> {}

/*
 * Style.
 */

const StyledFormDiv = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, auto);
  grid-gap: 10px;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 20px;
`;

/*
 * Component.
 */

export const SettingsCustomSmtpField: FC<SettingsCustomSmtpFieldProps> = (props) => {
  const smtpConfig = useMemo(
    () =>
      _.defaults(props.edited.preferences.smtp, {
        enabled: false,
        user: '',
        password: '',
        host: '',
        port: 25,
        encryption: false,
      }),
    [props.edited.preferences.smtp],
  );

  const canTestConnection = Boolean(
    smtpConfig.enabled && smtpConfig.user && smtpConfig.password && smtpConfig.host && smtpConfig.port,
  );

  const [safeSetTimeout, safeClearTimeout] = useTimeout();
  const {showBanner} = useSettingsBanners();

  usePushEvents((name, data) => {
    if (name !== 'check_smtp_result_completed' || !data) {
      return;
    }

    safeClearTimeout();

    if (hasProperty(data, 'status') && data.status === 'fail' && hasProperty(data, 'err')) {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: <Trans id="settingschannel-connectionfailed">Connection failed: {data.err}</Trans>,
      });
    } else {
      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: <Trans id="settingschannel-connectionsuccess">Connection successful!</Trans>,
      });
    }
  }, []);

  const onTestClick = async () => {
    if (/^\*+$/.test(smtpConfig.password)) {
      showBanner({
        type: SettingsBannersEnum.WARNING,
        message: <Trans id="settingschannel-inputpassword">Please input your password again.</Trans>,
      });
      return;
    }

    await postAsync(`${props.edited.url}/check_smtp_config`, smtpConfig);

    safeSetTimeout(() => {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: <Trans id="settingschannel-servertimeout">Timeout: server did not respond within 10s</Trans>,
      });
    }, 20 * 1000);
  };

  return (
    <SettingsAccordionFieldLine
      title={<Trans id="settingschannel-customsmtp-title">Custom SMTP</Trans>}
      subtitle={
        <Trans id="settingschannel-customsmtp-subtitle">
          This server will be used to send outgoing emails
        </Trans>
      }
      summaryValue={renderSummaryValueForBoolean(smtpConfig.enabled)}
    >
      <SettingsFieldLine
        title={<Trans id="settingschannel-enable">Enable</Trans>}
        hasNoBorder
        content={
          <Toggle
            isChecked={smtpConfig.enabled}
            onChange={(newValue) => {
              props.onPreferencesChange({
                smtp: {
                  ...smtpConfig,
                  enabled: newValue,
                },
              });
            }}
          />
        }
      />
      {smtpConfig.enabled && (
        <StyledFormDiv>
          <TextBox
            size={VisualSizesEnum.MEDIUM}
            label={<Trans id="settingschannel-smtp-host">Host</Trans>}
            value={smtpConfig.host}
            onChange={(newValue) => props.onPreferencesChange({smtp: {...smtpConfig, host: newValue}})}
          />
          <NumberInputBox
            size={VisualSizesEnum.MEDIUM}
            label={<Trans id="settingschannel-smtp-port">Port</Trans>}
            value={smtpConfig.port}
            onChange={(newValue) => {
              if (!newValue) {
                return;
              }

              props.onPreferencesChange({smtp: {...smtpConfig, port: newValue}});
            }}
          />
          <TextBox
            size={VisualSizesEnum.MEDIUM}
            label={<Trans id="settingschannel-smtp-login">Login</Trans>}
            value={smtpConfig.user}
            onChange={(newValue) => props.onPreferencesChange({smtp: {...smtpConfig, user: newValue}})}
          />
          <PasswordBox
            size={VisualSizesEnum.MEDIUM}
            label={<Trans id="settingschannel-smtp-password">Password</Trans>}
            value={smtpConfig.password}
            onChange={(newValue) => props.onPreferencesChange({smtp: {...smtpConfig, password: newValue}})}
          />
        </StyledFormDiv>
      )}
      {smtpConfig.enabled && (
        <SettingsFieldLine
          title={<Trans id="settingschannel-smtp-ssl">Connect with SSL/TLS encryption</Trans>}
          hasNoBorder
          content={
            <Toggle
              isChecked={smtpConfig.encryption}
              onChange={(newValue) => {
                props.onPreferencesChange({
                  smtp: {
                    ...smtpConfig,
                    encryption: newValue,
                  },
                });
              }}
            />
          }
        />
      )}
      {smtpConfig.enabled && (
        <StyledButtonWrapper>
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() => {
              onTestClick();
            }}
            isDisabled={!canTestConnection}
          >
            <Trans id="settingschannel-smtp-testconnection">Test connection</Trans>
          </Button>
        </StyledButtonWrapper>
      )}
    </SettingsAccordionFieldLine>
  );
};
